import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import {
  EntityProps,
  EntityState,
  removeEntity,
  setAllEntities,
  updateEntity,
  upsertEntity,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';

import { Modalité } from '../models/modalité.model';
import { ModalitéCreateRequest } from '../requests/modalité-create-request.model';
import { ModalitéUpdateRequest } from '../requests/modalité-update-request.model';
import { ModalitéService } from '../services/modalité.service';
import { v7 as uuidv7 } from 'uuid';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';

export function avecMéthodesModalités<_>() {
  return signalStoreFeature(
    {
      state: type<EntityState<Modalité>>(),
      props: type<EntityProps<Modalité>>(),
    },
    withMethods((store) => ({
      chargerModalités: (state: { modalités: Modalité[] }) => patchState(store, setAllEntities(state.modalités)),
      ajouterModalité: (state: { modalité: Modalité }) => patchState(store, upsertEntity(state.modalité)),
      modifierModalité: (state: { id: string; modalité: Partial<Modalité> }) =>
        patchState(store, updateEntity({ id: state.id, changes: state.modalité })),
      supprimerModalité: (id: string) => patchState(store, removeEntity(id)),
    })),
    withMethods((store, service = inject(ModalitéService)) => ({
      charger: rxMethod<string>(
        pipe(
          switchMap((idCartographie) =>
            service.trouverTous(idCartographie).pipe(
              tapResponse({
                next: (result) => store.chargerModalités({ modalités: result.items }),
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la récupération des modalités, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      ajouter: rxMethod<{
        data: ModalitéCreateRequest;
        next: () => void;
      }>(
        pipe(
          switchMap(({ data, next }) =>
            service.ajouter(data).pipe(
              tapResponse({
                next: () => next(),
                error: (err) => {
                  console.error("Error lors de l'ajout des modalités : ", err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de l'ajout d'une modalité, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        id: string;
        data: ModalitéUpdateRequest;
        next: () => void;
      }>(
        pipe(
          switchMap(({ id, data, next }) =>
            service.modifier(id, data).pipe(
              tapResponse({
                next: () => next(),
                error: (err) => {
                  console.error("Error lors de la modification d'une modalité : ", err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la modification d'une modalité, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      supprimer: rxMethod<{
        id: string;
        next: () => void;
      }>(
        pipe(
          switchMap(({ id, next }) =>
            service.supprimer(id).pipe(
              tapResponse({
                next: () => {
                  store.supprimerModalité(id);
                  next();
                },
                error: (err) => {
                  console.error("Error lors de la suppression d'une modalité : ", err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la suppression de la modalité, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
    })),
  );
}
